/* ============================================================
   DatabaseEmbed — 루트 database 를 본문에 인라인 임베드
   - 노션 "링크된 DB 뷰"를 LinkedDatabaseView 커스텀 블록이 렌더할 때 사용.
   - 자기 데이터는 전부 Supabase db_* 테이블에서 별도 로드한다.
     → 본문 Yjs 문서에 아무것도 쓰지 않는다(비파괴). 협업/직렬화 충돌 없음.

   2026-07-06 확장 — 노션 인라인 뷰 동등 렌더:
   - db_views 를 조회해 뷰 탭(표/보드/캘린더/갤러리/리스트/타임라인) 렌더.
     DatabasePage 와 동일 파이프라인(applyPipeline: 뷰 config 의 filter/sort)과
     동일 뷰 컴포넌트(BoardView/CalendarView/GalleryView/ListView/TimelineView)를 재사용.
   - DB 제목·아이콘 헤더(+ DB 페이지 링크) + '새로 만들기'(useDatabase.createRow 재사용,
     권한은 RLS 에 위임).
   - 폴백(무회귀): DB 페이지 매핑 실패 · 뷰 로드 실패 · 지원 뷰 없음 → 기존 읽기 전용
     플랫 표(FlatEmbed, 종전 구현 그대로). databaseId 자체가 없으면 안내 박스.
   ============================================================ */
import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { supabase } from '../../supabaseClient';
import { useDatabase } from './useDatabase';
import { useRelationContext } from './useRelationContext';
import { applyPipeline } from './viewPipeline';
import BoardView from './BoardView';
import CalendarView from './CalendarView';
import GalleryView from './GalleryView';
import ListView from './ListView';
import TimelineView from './TimelineView';
import { makeGetValue, personNames, relationIds, relationTitles, UUID_RE } from './computeValue';
import './database.css';

const ws = () => supabase.schema('workspace');

/* select 옵션 color → 태그 클래스 (database.css 재사용) */
const COLOR_CLASS = {
  gray: 'tg', default: 'tg',
  blue: 'tbl', green: 'tgr', yellow: 'tyl',
  orange: 'tor', purple: 'tpu', red: 'trd', pink: 'trd', brown: 'tor',
};

function optClass(col, value) {
  const opt = (col.options || []).find((o) => o.name === value);
  return COLOR_CLASS[opt?.color] || 'tg';
}

/* 임베드에서 렌더 가능한 뷰 타입 (chart/form 은 전체 페이지 전용 — 탭에서 제외) */
const EMBED_VIEW_TYPES = ['table', 'board', 'calendar', 'gallery', 'list', 'timeline'];
const VIEW_LABEL = {
  table: '표', board: '보드', calendar: '캘린더', gallery: '갤러리',
  list: '리스트', timeline: '타임라인', chart: '차트', form: '폼',
};

/* 읽기 전용 셀 표시 — 편집 input 없이 텍스트/태그/링크로만 */
function ReadCell({ col, row, people, titles }) {
  if (col.type === 'title') {
    return (
      <span className="db-embed-title">
        <span className="db-embed-emoji">{row.icon || '📄'}</span>
        <Link to={`/page/${row.page_id}`} className="db-embed-link">
          {row.title || '제목 없음'}
        </Link>
      </span>
    );
  }

  const value = row.properties?.[col.key];

  if (value == null || value === '') return <span className="db-embed-empty" />;

  if (col.type === 'select') {
    return <span className={`tag ${optClass(col, value)}`}>{String(value)}</span>;
  }

  if (col.type === 'checkbox') {
    return <span>{value ? '☑' : '☐'}</span>;
  }

  if (col.type === 'url') {
    const url = String(value);
    return (
      <a className="db-embed-link" href={url} target="_blank" rel="noreferrer">
        {url}
      </a>
    );
  }

  // person — uuid 배열을 멤버 이름으로(batch 조회 결과 people). 레거시 평문은 그대로.
  if (col.type === 'person') {
    return <span>{personNames(value, people).join(', ')}</span>;
  }

  // relation — 참조 행 page id 를 제목으로(batch 조회 결과 titles).
  // 조회에 없는 uuid(삭제/RLS 접근 불가)는 원시 uuid 대신 "(접근 불가)" 폴백. 레거시 평문은 그대로.
  if (col.type === 'relation') {
    const names = relationTitles(value, titles)
      .map((s) => (UUID_RE.test(String(s)) ? '(접근 불가)' : s));
    return <span>{names.join(', ')}</span>;
  }

  // date / number / text / 기타 — 평문(배열은 콤마 결합)
  const text = Array.isArray(value) ? value.join(', ') : String(value);
  return <span>{text}</span>;
}

/* 읽기 전용 표 렌더 (플랫 폴백 + 뷰 모드 '표' 탭 공용) */
function EmbedTable({ columns, rows, people, titles }) {
  return (
    <table>
      <thead>
        <tr>
          {columns.map((c) => (
            <th key={c.key}>{c.name}</th>
          ))}
        </tr>
      </thead>
      <tbody>
        {rows.length === 0 ? (
          <tr>
            <td colSpan={columns.length} className="db-embed-empty-row">
              행이 없습니다.
            </td>
          </tr>
        ) : (
          rows.map((row) => (
            <tr key={row.page_id}>
              {columns.map((c) => (
                <td key={c.key}>
                  <div className="db-embed-cell">
                    <ReadCell col={c} row={row} people={people} titles={titles} />
                  </div>
                </td>
              ))}
            </tr>
          ))
        )}
      </tbody>
    </table>
  );
}

/* person/relation 원시 uuid 를 이름/제목으로 풀기 위한 1회 batch 조회
   (useRelationContext 의 조회 패턴 축소판 — 임베드는 경량 유지, 실패해도 무시) */
async function fetchDisplayMaps(columns, rows) {
  const personCols = columns.filter((c) => c.type === 'person');
  const relCols = columns.filter((c) => c.type === 'relation');
  const personIds = new Set();
  const relPageIds = new Set();
  rows.forEach((r) => {
    personCols.forEach((c) => {
      const v = r.properties?.[c.key];
      (Array.isArray(v) ? v : []).forEach((id) => {
        if (UUID_RE.test(String(id))) personIds.add(String(id));
      });
    });
    relCols.forEach((c) => {
      relationIds(r.properties?.[c.key]).forEach((id) => {
        if (UUID_RE.test(String(id))) relPageIds.add(String(id));
      });
    });
  });

  const people = new Map(); // user_id → member
  if (personIds.size) {
    const { data } = await ws()
      .from('members')
      .select('user_id, display_name')
      .in('user_id', [...personIds]);
    (data || []).forEach((m) => {
      if (m.user_id && !people.has(m.user_id)) people.set(m.user_id, m);
    });
  }

  const titles = new Map(); // row page id → { title, icon }
  if (relPageIds.size) {
    const { data } = await ws()
      .from('pages')
      .select('id, title, icon')
      .in('id', [...relPageIds]);
    (data || []).forEach((p) => titles.set(p.id, { title: p.title, icon: p.icon }));
  }

  return { people, titles };
}

/* ---------------- 플랫 표 폴백 (종전 DatabaseEmbed 구현 그대로) ---------------- */
function FlatEmbed({ databaseId }) {
  const [state, setState] = useState({
    loading: true, error: null, columns: [], rows: [],
    people: new Map(), titles: new Map(),
  });

  useEffect(() => {
    let active = true;
    if (!databaseId) {
      setState({
        loading: false, error: 'missing', columns: [], rows: [],
        people: new Map(), titles: new Map(),
      });
      return () => { active = false; };
    }
    setState((s) => ({ ...s, loading: true, error: null }));
    (async () => {
      try {
        // 스키마(컬럼)
        const { data: sch, error: schErr } = await ws()
          .from('db_schemas')
          .select('columns')
          .eq('database_id', databaseId)
          .maybeSingle();
        if (schErr) throw schErr;
        const columns = sch && Array.isArray(sch.columns) && sch.columns.length
          ? sch.columns
          : [{ key: 'title', name: '이름', type: 'title' }];

        // 행(db_rows) + 행 페이지(title/icon/position)
        const { data: rrows, error: rErr } = await ws()
          .from('db_rows')
          .select('page_id, database_id, properties')
          .eq('database_id', databaseId);
        if (rErr) throw rErr;

        const ids = (rrows || []).map((r) => r.page_id);
        const rowPages = {};
        if (ids.length) {
          const { data: rps } = await ws()
            .from('pages')
            .select('id, title, icon, position')
            .in('id', ids);
          (rps || []).forEach((rp) => { rowPages[rp.id] = rp; });
        }
        const rows = (rrows || [])
          .map((r) => ({
            page_id: r.page_id,
            properties: r.properties || {},
            title: rowPages[r.page_id]?.title || '',
            icon: rowPages[r.page_id]?.icon || '📄',
            position: rowPages[r.page_id]?.position ?? 0,
          }))
          .sort((a, b) => a.position - b.position);

        // person 이름 / relation 제목 batch 조회 (실패해도 표는 그대로 표시)
        let people = new Map();
        let titles = new Map();
        try {
          ({ people, titles } = await fetchDisplayMaps(columns, rows));
        } catch (e2) { /* noop — uuid 폴백 표시 */ }

        if (!active) return;
        setState({ loading: false, error: null, columns, rows, people, titles });
      } catch (e) {
        if (!active) return;
        setState({
          loading: false, error: e.message || String(e), columns: [], rows: [],
          people: new Map(), titles: new Map(),
        });
      }
    })();
    return () => { active = false; };
  }, [databaseId]);

  // 폴백: 매핑 실패(빈 id) 또는 로드 실패 → 안내 박스 (에디터/Yjs 영향 없음)
  if (!databaseId || state.error) {
    return (
      <div className="db-embed db-embed-fallback">
        이 뷰는 가져온 데이터베이스를 찾을 수 없습니다.
      </div>
    );
  }

  if (state.loading) {
    return <div className="db-embed db-embed-fallback">데이터베이스를 불러오는 중…</div>;
  }

  const { columns, rows, people, titles } = state;

  return (
    <div className="db db-embed">
      <EmbedTable columns={columns} rows={rows} people={people} titles={titles} />
    </div>
  );
}

/* ---------------- 뷰 렌더 모드 (db_views 기반, 2026-07-06) ---------------- */
function EmbedViews({ databaseId, dbPageId }) {
  // DatabasePage 와 동일 데이터 훅 재사용 — 행 추가(createRow)/셀 갱신(updateCell,
  // 보드 그룹 이동)/뷰 목록까지 전부 같은 로직. 쓰기 권한은 RLS 가 판정.
  const {
    loading, error, page, columns, views, rows, createRow, updateCell,
  } = useDatabase(dbPageId);
  const dbx = useRelationContext(page, columns, rows);

  const [activeViewId, setActiveViewId] = useState(null);
  // 보드 그룹/캘린더·타임라인 날짜 기준은 로컬 오버라이드 — 원본 뷰 config 는 건드리지 않는다.
  const [viewOverrides, setViewOverrides] = useState({});

  const embedViews = useMemo(
    () => (views || []).filter((v) => EMBED_VIEW_TYPES.includes(v.view_type)),
    [views]
  );

  const activeView = useMemo(() => {
    if (!embedViews.length) return null;
    return embedViews.find((v) => v.id === activeViewId) || embedViews[0];
  }, [embedViews, activeViewId]);

  const effectiveView = useMemo(() => {
    if (!activeView) return null;
    const ov = viewOverrides[activeView.id];
    if (!ov) return activeView;
    return { ...activeView, config: { ...(activeView.config || {}), ...ov } };
  }, [activeView, viewOverrides]);

  const setOverride = (patch) => {
    if (!activeView) return;
    setViewOverrides((prev) => ({
      ...prev,
      [activeView.id]: { ...(prev[activeView.id] || {}), ...patch },
    }));
  };

  // 뷰 config 의 filter/sort 적용 — DatabasePage 와 동일 파이프라인/값 접근자
  const filters = useMemo(
    () => (Array.isArray(effectiveView?.config?.filters) ? effectiveView.config.filters : []),
    [effectiveView]
  );
  const sorts = useMemo(
    () => (Array.isArray(effectiveView?.config?.sorts) ? effectiveView.config.sorts : []),
    [effectiveView]
  );
  const getValue = useMemo(() => makeGetValue(columns, dbx), [columns, dbx]);
  const visibleRows = useMemo(
    () => applyPipeline(rows, columns, { filters, sorts, search: '', getValue }),
    [rows, columns, filters, sorts, getValue]
  );

  if (loading) {
    return <div className="db-embed db-embed-fallback">데이터베이스를 불러오는 중…</div>;
  }

  // 로드 실패 · 지원 뷰 없음 → 종전 플랫 표 폴백 (무회귀)
  if (error || !page || embedViews.length === 0) {
    return <FlatEmbed databaseId={databaseId} />;
  }

  const vt = effectiveView?.view_type;

  return (
    <div className="db db-embed db-embed-rich">
      {/* DB 제목·아이콘 헤더 + 새로 만들기 */}
      <div className="db-embed-head">
        <Link to={`/page/${dbPageId}`} className="db-embed-db-title" title="데이터베이스 열기">
          <span className="db-embed-emoji">{page.icon || '🗂️'}</span>
          <span className="db-embed-db-name">{page.title || '제목 없는 데이터베이스'}</span>
        </Link>
        <button className="db-embed-new" onClick={() => createRow()}>＋ 새로 만들기</button>
      </div>

      {/* 뷰 탭 — 여러 개면 전환 UI (노션처럼 뷰 이름 표시) */}
      {embedViews.length > 1 && (
        <div className="vtabs db-embed-vtabs">
          {embedViews.map((v) => (
            <button
              key={v.id}
              className={`vt${activeView && v.id === activeView.id ? ' on' : ''}`}
              onClick={() => setActiveViewId(v.id)}
            >
              {v.name || VIEW_LABEL[v.view_type] || v.view_type}
            </button>
          ))}
        </div>
      )}

      <div className="db-embed-body">
        {vt === 'table' && (
          <EmbedTable
            columns={columns}
            rows={visibleRows}
            people={dbx.peopleById}
            titles={dbx.titleById}
          />
        )}
        {vt === 'board' && (
          <BoardView
            columns={columns}
            rows={visibleRows}
            view={effectiveView}
            peopleById={dbx.peopleById}
            updateCell={updateCell}
            createRow={createRow}
            onChangeGroupBy={(key) => setOverride({ groupBy: key })}
          />
        )}
        {vt === 'calendar' && (
          <CalendarView
            columns={columns}
            rows={visibleRows}
            view={effectiveView}
            onChangeDateKey={(key) => setOverride({ dateKey: key })}
          />
        )}
        {vt === 'gallery' && (
          <GalleryView columns={columns} rows={visibleRows} peopleById={dbx.peopleById} createRow={createRow} />
        )}
        {vt === 'list' && (
          <ListView columns={columns} rows={visibleRows} peopleById={dbx.peopleById} createRow={createRow} />
        )}
        {vt === 'timeline' && (
          <TimelineView
            columns={columns}
            rows={visibleRows}
            view={effectiveView}
            onChangeDateKey={(key) => setOverride({ dateKey: key })}
          />
        )}
      </div>
    </div>
  );
}

/* ---------------- 엔트리 — databaseId → DB 페이지 매핑 후 모드 결정 ---------------- */
export default function DatabaseEmbed({ databaseId }) {
  // databaseId(루트 db UUID) → type='database' 페이지 해석.
  // pages.database_id 가 별도 지정된 DB(id ≠ database_id)도 커버하기 위해 or 매칭.
  const [res, setRes] = useState({ loading: true, dbPageId: null });

  useEffect(() => {
    let active = true;
    if (!databaseId || !UUID_RE.test(String(databaseId))) {
      setRes({ loading: false, dbPageId: null });
      return () => { active = false; };
    }
    setRes({ loading: true, dbPageId: null });
    (async () => {
      try {
        const { data, error } = await ws()
          .from('pages')
          .select('id, database_id')
          .eq('type', 'database')
          .or(`id.eq.${databaseId},database_id.eq.${databaseId}`)
          .limit(2);
        if (!active) return;
        if (error) throw error;
        const list = data || [];
        const hit = list.find((p) => p.id === databaseId) || list[0] || null;
        setRes({ loading: false, dbPageId: hit ? hit.id : null });
      } catch (e) {
        // 매핑 조회 실패 — 플랫 표 폴백 경로로 (그쪽에서 재시도/에러 안내)
        if (active) setRes({ loading: false, dbPageId: null });
      }
    })();
    return () => { active = false; };
  }, [databaseId]);

  if (!databaseId) {
    return (
      <div className="db-embed db-embed-fallback">
        이 뷰는 가져온 데이터베이스를 찾을 수 없습니다.
      </div>
    );
  }

  if (res.loading) {
    return <div className="db-embed db-embed-fallback">데이터베이스를 불러오는 중…</div>;
  }

  // DB 페이지 매핑 실패(삭제/RLS/비정상 id) → 종전 플랫 표 폴백 (무회귀)
  if (!res.dbPageId) return <FlatEmbed databaseId={databaseId} />;

  return <EmbedViews databaseId={databaseId} dbPageId={res.dbPageId} />;
}
